import React from "react";
import {
  TreePine,
  Star,
  ArrowRight,
  Gift,
  Mail,
  Truck,
  Heart,
  ShoppingBag,
  X,
  Check,
} from "lucide-react";
import {
  PACKAGES,
  ART_DISCLAIMER,
  money,
  priceSummary,
} from "../shared/catalog.js";

export {
  TreePine,
  Star,
  ArrowRight,
  Gift,
  Mail,
  Truck,
  Heart,
  ShoppingBag,
  X,
  Check,
};
export function Brand({ small = false }) {
  return (
    <a className={`brand ${small ? "small" : ""}`} href="/">
      <img
        className="brand-logo"
        src={small ? "/images/logo-full.png" : "/images/logo.png"}
        alt="Whimsy Delivered"
      />
    </a>
  );
}
export function Button({ children, className = "", ...props }) {
  return (
    <button className={`button ${className}`} {...props}>
      {children}
    </button>
  );
}
export function ErrorMessage({ children }) {
  return children ? (
    <p className="error-message" role="alert">
      {children}
    </p>
  ) : null;
}
export function SectionIntro({ eyebrow, title, children }) {
  return (
    <div className="section-intro">
      <span className="eyebrow">{eyebrow}</span>
      <h2>{title}</h2>
      {children && <p>{children}</p>}
    </div>
  );
}
export function PackageArt({ index = 0, small = false }) {
  if (PACKAGES[index].kind === "letter")
    return (
      <div
        className={`package-art art-letter ${small ? "art-small" : ""}`}
        role="img"
        aria-label="A cream envelope from Santa sealed with red wax"
      >
        <svg viewBox="0 0 260 170" aria-hidden="true">
          <rect x="1" y="1" width="258" height="168" rx="3" fill="#ece2cc" stroke="#d6c8ac" />
          <path d="M1 1 L120 92 L1 169 Z" fill="#e7dcc2" />
          <path d="M259 1 L140 92 L259 169 Z" fill="#e7dcc2" />
          <path d="M1 169 L130 82 L259 169 Z" fill="#eee5d2" stroke="#d6c8ac" />
          <path d="M1 1 L130 98 L259 1 Z" fill="#f2ead9" stroke="#d6c8ac" />
          <circle cx="130" cy="98" r="19" fill="#922f33" />
          <path
            d="M130 86 l3.5 7.5 8 .9-6 5.5 1.7 8-7.2-4.1-7.2 4.1 1.7-8-6-5.5 8-.9z"
            fill="#e9c9a0"
          />
        </svg>
      </div>
    );
  return (
    <div
      className={`package-art art-${index} ${small ? "art-small" : ""}`}
      role="img"
      aria-label={`Illustrated concept for ${PACKAGES[index].name}. ${ART_DISCLAIMER}`}
    >
      {!small && <span className="art-disclaimer">{ART_DISCLAIMER}</span>}
    </div>
  );
}
export function PackageCard({ pack, index, onChoose, disabled = false }) {
  return (
    <article
      className={`package-card ${pack.featured ? "featured" : ""} ${pack.kind === "letter" ? "package-wide" : ""}`}
    >
      {pack.featured && (
        <div className="package-ribbon">
          <Star size={11} /> A LITTLE MORE WONDER
        </div>
      )}
      <PackageArt index={index} />
      <div className="package-copy">
        <span className="eyebrow">{pack.tag}</span>
        <h3>{pack.name}</h3>
        <p>{pack.description}</p>
        <ul>
          {pack.contents.map((c) => (
            <li key={c}>
              <Check size={14} />
              {c}
            </li>
          ))}
        </ul>
        <div className="package-price">
          <strong>{money(pack.price)}</strong>
          <span>{priceSummary(pack)}</span>
        </div>
        <Button
          className={pack.featured ? "" : "button-outline"}
          disabled={disabled}
          onClick={() => onChoose(pack.id)}
        >
          {disabled ? "Orders closed" : "Choose this magic"}
          <ArrowRight size={16} />
        </Button>
      </div>
    </article>
  );
}
export function Modal({ title, onClose, children, wide = false }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const previous = document.activeElement;
    const bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    ref.current.showModal();
    return () => {
      document.body.style.overflow = bodyOverflow;
      previous?.focus();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      aria-label={title}
      className={`modal ${wide ? "modal-wide" : ""}`}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className="modal-header">
        <h2>{title}</h2>
        <button
          className="icon-button"
          aria-label="Close dialog"
          onClick={onClose}
        >
          <X size={22} />
        </button>
      </div>
      {children}
    </dialog>
  );
}
export function Field({ label, children, hint, controlId }) {
  const generatedId = React.useId();
  const id = controlId || generatedId;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {controlId
        ? children
        : React.cloneElement(children, {
            id,
            "aria-describedby": hint ? `${id}-hint` : undefined,
          })}
      {hint && <small id={`${id}-hint`}>{hint}</small>}
    </div>
  );
}
